import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MailOpen } from "lucide-react";
import { BareLayout } from "../components/Layout";
import { Btn, Card, Notice } from "../components/kit";
import { Loading } from "../components/common";
import { api, parseError } from "../lib/api";
import { NEXT_KEY, useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/format";

export default function Invite() {
  const { t } = useTranslation();
  const { token } = useParams();
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/invitations/public/${token}`).then((r) => setPub(r.data)); }, [token]);
  useEffect(() => {
    if (me && pub?.valid) api.get(`/invitations/${token}`).then((r) => setDetail(r.data)).catch((e) => setErr(parseError(e).code));
  }, [me, pub, token]);
  if (pub === null || me === undefined) return <Loading />;
  const accept = async () => {
    try {
      const { data } = await api.post(`/invitations/${token}/accept`);
      await refresh();
      navigate(data.target.type === "dog" ? `/owner/dogs/${data.target.id}` : "/practice");
    } catch (e) { setErr(parseError(e).code); }
  };
  const go = (path) => { sessionStorage.setItem(NEXT_KEY, `/invite/${token}`); navigate(path); };
  return (
    <BareLayout>
      <Card className="mx-auto mt-6 max-w-lg space-y-4 fade-up" data-testid="invite-page">
        <span className="inline-flex rounded-xl bg-[hsl(var(--peach))] p-3"><MailOpen className="h-6 w-6 text-[hsl(14_56%_34%)]" /></span>
        {!pub.valid || err === "invitation_invalid" ? (
          <p role="alert" data-testid="invite-invalid" className="text-lg font-bold">{t("err.invitation_invalid")}</p>
        ) : err === "invitation_other_email" ? (
          <p role="alert" data-testid="invite-other-email" className="text-lg font-bold">{t("err.invitation_other_email")}</p>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold">{pub.kind === "owner" ? t("invite.owner_title", { inviter: pub.inviter_name }) : t("invite.member_title", { inviter: pub.inviter_name })}</h1>
            <p data-testid="invite-workspace">{t("invite.from", { ws: pub.workspace_name })}</p>
            <div><p className="font-semibold">{t("invite.actions")}</p><ul className="list-disc pl-5 text-sm">{pub.actions.map((a) => <li key={a}>{t(`invite.action_${a}`)}</li>)}</ul></div>
            <p className="text-sm text-muted-foreground" data-testid="invite-expiry">{t("invite.valid_until", { date: fmtDateTime(pub.expires_at) })}</p>
            {!me ? (
              <>
                <Notice testid="invite-signin-required">{t("invite.sign_in_required")}</Notice>
                <div className="flex flex-wrap gap-2">
                  <Btn testid="invite-sign-in-btn" onClick={() => go("/login")}>{t("auth.sign_in")}</Btn>
                  <Btn variant="outline" testid="invite-sign-up-btn" onClick={() => go("/register")}>{t("auth.sign_up")}</Btn>
                </div>
              </>
            ) : detail ? (
              <>
                {detail.dog_name && <p className="text-lg" data-testid="invite-dog-name">{t("invite.for_dog", { name: detail.dog_name })}</p>}
                <Btn testid="invite-accept-btn" onClick={accept}>{t("invite.accept")}</Btn>
              </>
            ) : <Loading />}
          </>
        )}
        {me && <Link to="/" className="block text-sm font-semibold text-primary underline">{t("invite.go_home")}</Link>}
      </Card>
    </BareLayout>
  );
}
